import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  DollarSign, Plus, Search, Filter, Download, FileText, 
  CheckCircle, Clock, AlertTriangle, TrendingUp, TrendingDown,
  Loader2, Eye, Pencil, Trash2, Receipt, CreditCard, PiggyBank,
  Calendar, User, Building, ChevronDown, ChevronUp, BarChart3,
  ChevronsUpDown, Check, Percent
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { facturasAPI, contactosAPI, expedientesAPI, tareasDespachoAPI, configuracionAPI, inmueblesAPI } from '../api';
import { toast } from '../hooks/use-toast';
import HonorariosTab from '../components/HonorariosTab';
import FiscalidadTab from '../components/FiscalidadTab';
import { Landmark } from 'lucide-react';
import FiscalStateBadge from '../components/Verifactu/FiscalStateBadge';
import CotitularesSelector from '../components/CotitularesSelector';
import { useAuth } from '../contexts/AuthContext';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend
} from 'recharts';

const ESTADOS_FACTURA = [
  { value: 'pendiente', label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  { value: 'parcial', label: 'Pago Parcial', color: 'bg-blue-100 text-blue-800', icon: CreditCard },
  { value: 'pagada', label: 'Pagada', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  { value: 'vencida', label: 'Vencida', color: 'bg-red-100 text-red-800', icon: AlertTriangle },
  { value: 'anulada', label: 'Anulada', color: 'bg-gray-100 text-gray-800', icon: AlertTriangle },
];

const TIPOS_FACTURA = [
  { value: 'proforma', label: 'Proforma' },
  { value: 'factura', label: 'Factura' },
  { value: 'presupuesto', label: 'Presupuesto' },
];

// Las rectificativas no se crean desde el alta normal (se emiten desde la factura
// que rectifican), pero sí se pueden filtrar en el listado.
const TIPOS_FILTRO = [
  ...TIPOS_FACTURA,
  { value: 'rectificativa', label: 'Rectificativa' },
];

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-ES');
};

// ============================================================================
// COMBOBOX: Buscador de Clientes con busqueda en servidor
// ============================================================================
const ClienteCombobox = ({ value, onChange, contactos: initialContactos = [], onSelectedChange }) => {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchResults, setSearchResults] = React.useState([]);
  const [searching, setSearching] = React.useState(false);
  const [stickySelected, setStickySelected] = React.useState(null);
  const searchTimeout = React.useRef(null);
  const inputRef = React.useRef(null);

  const displayList = searchQuery.length >= 2 ? (Array.isArray(searchResults) ? searchResults : []) : initialContactos;
  const allKnown = React.useMemo(() => {
    const map = new Map();
    const safeContactos = Array.isArray(initialContactos) ? initialContactos : [];
    const safeResults = Array.isArray(searchResults) ? searchResults : [];
    safeContactos.forEach(c => { if (c && c.id != null) map.set(String(c.id), c); });
    safeResults.forEach(c => { if (c && c.id != null) map.set(String(c.id), c); });
    if (stickySelected && stickySelected.id != null) map.set(String(stickySelected.id), stickySelected);
    return map;
  }, [initialContactos, searchResults, stickySelected]);
  const selected = value ? allKnown.get(String(value)) : null;

  React.useEffect(() => { if (onSelectedChange) onSelectedChange(selected || null); }, [selected, onSelectedChange]);

  // Si el value cambia desde fuera (p.ej. autocompletado por expediente)
  // y no lo conocemos, traerlo del backend para mostrar nombre/NIF.
  React.useEffect(() => {
    if (!value) { setStickySelected(null); return; }
    if (allKnown.get(String(value))) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await contactosAPI.getById(value);
        const c = res.data?.data || res.data;
        if (!cancelled && c && c.id) setStickySelected(c);
      } catch (e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [value]);

  React.useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (query.length < 2) { setSearchResults([]); return; }
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await contactosAPI.getAll({ search: query, limit: 50 });
        const data = res.data?.data || res.data || [];
        setSearchResults(Array.isArray(data) ? data : []);
      } catch (e) { console.error('Error buscando clientes:', e); }
      setSearching(false);
    }, 300);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className={`w-full justify-between font-normal ${selected ? 'border-green-500 bg-green-50 text-green-900 hover:bg-green-100' : ''}`} type="button" data-testid="cliente-combobox-trigger">
          <span className="flex items-center gap-2 truncate">
            {selected && <Check className="h-4 w-4 text-green-600 shrink-0" />}
            <span className="truncate">{selected ? `${selected.nombre_completo || selected.nombre}${selected.nif_cif ? ` (${selected.nif_cif})` : ''}` : 'Buscar cliente...'}</span>
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div className="flex items-center border-b px-3 py-2">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <input ref={inputRef} type="text" className="flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400"
            placeholder="Buscar por nombre o NIF..." value={searchQuery} onChange={(e) => handleSearch(e.target.value)} />
          {searchQuery && <button type="button" className="ml-1 text-gray-400 hover:text-gray-600 text-xs" onClick={() => { setSearchQuery(''); setSearchResults([]); }}>&times;</button>}
        </div>
        <div className="max-h-[280px] overflow-y-auto">
          {searching && <div className="p-3 text-sm text-center text-gray-500">Buscando...</div>}
          {!searching && displayList.length === 0 && <div className="p-4 text-sm text-center text-gray-500">{searchQuery.length < 2 ? 'Escribe al menos 2 caracteres...' : 'No se encontraron clientes'}</div>}
          {displayList.map(c => (
            <div key={c.id} className={`flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-accent transition-colors ${String(value) === String(c.id) ? 'bg-accent/50' : ''}`}
              onClick={() => { setStickySelected(c); onChange(String(c.id)); setOpen(false); setSearchQuery(''); setSearchResults([]); }}>
              <Check className={`mr-2 h-4 w-4 flex-shrink-0 ${String(value) === String(c.id) ? 'opacity-100' : 'opacity-0'}`} />
              <span className="truncate">{c.nombre_completo || c.nombre}{c.nif_cif ? ` (${c.nif_cif})` : ''}</span>
            </div>
          ))}
        </div>
        {value && <div className="border-t px-3 py-2"><button type="button" className="text-xs text-red-500 hover:text-red-700" onClick={() => { onChange(''); setOpen(false); setSearchQuery(''); setSearchResults([]); }}>Quitar cliente</button></div>}
      </PopoverContent>
    </Popover>
  );
};

// ============================================================================
// COMBOBOX: Buscador de Expedientes con busqueda en servidor
// ============================================================================
const ExpedienteComboboxFacturas = ({ value, onChange, expedientes: initialExpedientes = [], onSelectedChange }) => {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchResults, setSearchResults] = React.useState([]);
  const [searching, setSearching] = React.useState(false);
  const [stickySelected, setStickySelected] = React.useState(null);
  const searchTimeout = React.useRef(null);
  const inputRef = React.useRef(null);

  const displayList = searchQuery.length >= 2 ? (Array.isArray(searchResults) ? searchResults : []) : initialExpedientes;
  const allKnown = React.useMemo(() => {
    const map = new Map();
    const safeExpedientes = Array.isArray(initialExpedientes) ? initialExpedientes : [];
    const safeResults = Array.isArray(searchResults) ? searchResults : [];
    safeExpedientes.forEach(e => { if (e && e.id != null) map.set(String(e.id), e); });
    safeResults.forEach(e => { if (e && e.id != null) map.set(String(e.id), e); });
    if (stickySelected && stickySelected.id != null) map.set(String(stickySelected.id), stickySelected);
    return map;
  }, [initialExpedientes, searchResults, stickySelected]);
  const selected = value ? allKnown.get(String(value)) : null;

  React.useEffect(() => { if (onSelectedChange) onSelectedChange(selected || null); }, [selected, onSelectedChange]);

  // Si el value cambia desde fuera y no lo conocemos, traerlo del backend.
  React.useEffect(() => {
    if (!value) { setStickySelected(null); return; }
    if (allKnown.get(String(value))) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await expedientesAPI.getById(value);
        const exp = res.data?.data || res.data;
        if (!cancelled && exp && exp.id) setStickySelected(exp);
      } catch (e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [value]);

  React.useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (query.length < 2) { setSearchResults([]); return; }
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await expedientesAPI.getAll({ search: query, limit: 50 });
        const data = Array.isArray(res.data) ? res.data : (res.data?.data || res.data?.expedientes || []);
        setSearchResults(data);
      } catch (e) { console.error('Error buscando expedientes:', e); }
      setSearching(false);
    }, 300);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className={`w-full justify-between font-normal ${selected ? 'border-green-500 bg-green-50 text-green-900 hover:bg-green-100' : ''}`} type="button" data-testid="expediente-combobox-trigger">
          <span className="flex items-center gap-2 truncate">
            {selected && <Check className="h-4 w-4 text-green-600 shrink-0" />}
            <span className="truncate">{selected ? `${selected.numero_expediente} - ${selected.titulo}` : 'Buscar expediente...'}</span>
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div className="flex items-center border-b px-3 py-2">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <input ref={inputRef} type="text" className="flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400"
            placeholder="Buscar por numero o nombre..." value={searchQuery} onChange={(e) => handleSearch(e.target.value)} />
          {searchQuery && <button type="button" className="ml-1 text-gray-400 hover:text-gray-600 text-xs" onClick={() => { setSearchQuery(''); setSearchResults([]); }}>&times;</button>}
        </div>
        <div className="max-h-[280px] overflow-y-auto">
          {searching && <div className="p-3 text-sm text-center text-gray-500">Buscando...</div>}
          {!searching && displayList.length === 0 && <div className="p-4 text-sm text-center text-gray-500">{searchQuery.length < 2 ? 'Escribe al menos 2 caracteres...' : 'No se encontraron expedientes'}</div>}
          {displayList.map(exp => (
            <div key={exp.id} className={`flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-accent transition-colors ${String(value) === String(exp.id) ? 'bg-accent/50' : ''}`}
              onClick={() => { setStickySelected(exp); onChange(String(exp.id), exp); setOpen(false); setSearchQuery(''); setSearchResults([]); }}>
              <Check className={`mr-2 h-4 w-4 flex-shrink-0 ${String(value) === String(exp.id) ? 'opacity-100' : 'opacity-0'}`} />
              <span className="truncate">{exp.numero_expediente} - {exp.titulo}</span>
            </div>
          ))}
        </div>
        {value && <div className="border-t px-3 py-2"><button type="button" className="text-xs text-red-500 hover:text-red-700" onClick={() => { onChange(''); setOpen(false); setSearchQuery(''); setSearchResults([]); }}>Quitar expediente</button></div>}
      </PopoverContent>
    </Popover>
  );
};

// ============================================================================
// COMPONENTE: Minutas en Contabilidad
// ============================================================================
const MinutasContabilidadTab = () => {
  const { user } = useAuth();
  const isAdmin = user?.rol === 'admin' || user?.rol === 'superadmin';
  const [loading, setLoading] = useState(true);
  const [anno, setAnno] = useState(new Date().getFullYear());
  const [datos, setDatos] = useState(null);
  const [evolucion, setEvolucion] = useState([]);
  const [viewMode, setViewMode] = useState('lista'); // lista, estadisticas
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadDatos();
  }, [anno]);

  useEffect(() => {
    loadEvolucion();
  }, []);

  const loadDatos = async () => {
    setLoading(true);
    try {
      const res = await tareasDespachoAPI.minutas.getEstadisticasContabilidad(anno);
      setDatos(res.data);
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEvolucion = async () => {
    try {
      const res = await tareasDespachoAPI.minutas.getEvolucion();
      setEvolucion(res.data?.evolucion || []);
    } catch (error) {
      console.error('Error cargando evolución:', error);
    }
  };

  const annoActual = new Date().getFullYear();
  const annos = Array.from({ length: 5 }, (_, i) => annoActual - i);

  const minutasFiltradas = React.useMemo(() => {
    if (!datos?.minutas || !searchTerm.trim()) return datos?.minutas || [];
    const s = searchTerm.toLowerCase();
    return datos.minutas.filter(m => 
      (m.numero_minuta?.toLowerCase().includes(s)) ||
      (m.expediente_numero?.toLowerCase().includes(s)) ||
      (m.tipo_costas?.toLowerCase().includes(s)) ||
      (m.procurador_nombre?.toLowerCase().includes(s))
    );
  }, [datos?.minutas, searchTerm]);

  const handleDeleteMinuta = async (minuta) => {
    if (!window.confirm(`¿Eliminar la minuta ${minuta.numero_minuta}? Esta acción no se puede deshacer.`)) return;
    try {
      await tareasDespachoAPI.minutas.delete(minuta.id);
      toast({ title: 'Minuta eliminada correctamente' });
      loadDatos();
    } catch (error) {
      const msg = error.response?.data?.detail || 'No se pudo eliminar';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-green-600" />
            Minutas - Previsión de Ingresos
          </h2>
          <p className="text-sm text-gray-600">Control de minutas tramitadas y previsión de ingresos del despacho</p>
        </div>
        <div className="flex gap-2">
          <Select value={anno.toString()} onValueChange={(v) => setAnno(parseInt(v))}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {annos.map(a => (
                <SelectItem key={a} value={a.toString()}>{a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            variant={viewMode === 'lista' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setViewMode('lista')}
          >
            <FileText className="w-4 h-4 mr-1" /> Lista
          </Button>
          <Button 
            variant={viewMode === 'estadisticas' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setViewMode('estadisticas')}
          >
            <BarChart3 className="w-4 h-4 mr-1" /> Estadísticas
          </Button>
        </div>
      </div>

      {/* Buscador */}
      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Buscar minuta, expediente, procurador..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        {searchTerm && <Button variant="ghost" size="sm" onClick={() => setSearchTerm('')}>Limpiar</Button>}
      </div>

      {/* Resumen */}
      {datos && (
        <div className="grid grid-cols-4 gap-4">
          <Card className="bg-blue-50">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-gray-600">Total Minutas {anno}</p>
              <p className="text-3xl font-bold text-blue-600">{datos.resumen.total_minutas}</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-gray-600">Importe Total</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(datos.resumen.importe_total)}</p>
            </CardContent>
          </Card>
          <Card className="bg-yellow-50">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-gray-600">Base Imponible</p>
              <p className="text-2xl font-bold text-yellow-600">{formatCurrency(datos.resumen.base_imponible)}</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-50">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-gray-600">IVA (21%)</p>
              <p className="text-2xl font-bold text-purple-600">{formatCurrency(datos.resumen.iva_total)}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {viewMode === 'estadisticas' ? (
        <div className="space-y-4">
          {/* Gráfico por meses */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Minutas por Mes - {anno}</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={datos?.por_mes || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="nombre_mes" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    formatter={(value, name) => [
                      name === 'cantidad' ? value : formatCurrency(value),
                      name === 'cantidad' ? 'Cantidad' : name === 'importe_total' ? 'Importe' : name
                    ]}
                  />
                  <Legend />
                  <Bar dataKey="cantidad" name="Cantidad" fill="#4F46E5" />
                  <Bar dataKey="importe_total" name="Importe Total" fill="#10B981" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Gráfico de evolución por años */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Evolución Anual de Minutas</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={evolucion}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="anno" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    formatter={(value, name) => [
                      name === 'cantidad' ? value : formatCurrency(value),
                      name === 'cantidad' ? 'Cantidad' : 'Importe'
                    ]}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="cantidad" name="Cantidad" stroke="#4F46E5" strokeWidth={2} />
                  <Line type="monotone" dataKey="importe_total" name="Importe" stroke="#10B981" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Gráfico por Tipo de Costas */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Desglose por Tipo de Costas - {anno}</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={datos?.por_tipo_costas?.filter(t => t.cantidad > 0) || []} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis dataKey="tipo" type="category" tick={{ fontSize: 10 }} width={180} />
                  <Tooltip 
                    formatter={(value, name) => [
                      name === 'cantidad' ? value : formatCurrency(value),
                      name === 'cantidad' ? 'Cantidad' : 'Importe'
                    ]}
                  />
                  <Legend />
                  <Bar dataKey="cantidad" name="Cantidad" fill="#8B5CF6" />
                  <Bar dataKey="importe_total" name="Importe Total" fill="#10B981" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Tabla por Tipo de Costas */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Desglose por Tipo de Costas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left">Tipo de Costas</th>
                      <th className="px-4 py-2 text-right">Cantidad</th>
                      <th className="px-4 py-2 text-right">Base Imponible</th>
                      <th className="px-4 py-2 text-right">IVA</th>
                      <th className="px-4 py-2 text-right">Importe Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {datos?.por_tipo_costas?.map((tipo) => (
                      <tr key={tipo.tipo} className={tipo.cantidad > 0 ? 'bg-purple-50' : ''}>
                        <td className="px-4 py-2 font-medium">{tipo.tipo}</td>
                        <td className="px-4 py-2 text-right">{tipo.cantidad}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(tipo.base_imponible)}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(tipo.iva)}</td>
                        <td className="px-4 py-2 text-right font-medium text-purple-600">{formatCurrency(tipo.importe_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Tabla por meses */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Desglose por Mes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left">Mes</th>
                      <th className="px-4 py-2 text-right">Cantidad</th>
                      <th className="px-4 py-2 text-right">Base Imponible</th>
                      <th className="px-4 py-2 text-right">IVA</th>
                      <th className="px-4 py-2 text-right">Importe Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {datos?.por_mes?.map((mes) => (
                      <tr key={mes.mes} className={mes.cantidad > 0 ? 'bg-green-50' : ''}>
                        <td className="px-4 py-2 font-medium">{mes.nombre_mes}</td>
                        <td className="px-4 py-2 text-right">{mes.cantidad}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(mes.base_imponible)}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(mes.iva)}</td>
                        <td className="px-4 py-2 text-right font-medium text-green-600">{formatCurrency(mes.importe_total)}</td>
                      </tr>
                    ))}
                    <tr className="bg-gray-100 font-bold">
                      <td className="px-4 py-2">TOTAL</td>
                      <td className="px-4 py-2 text-right">{datos?.resumen?.total_minutas}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(datos?.resumen?.base_imponible)}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(datos?.resumen?.iva_total)}</td>
                      <td className="px-4 py-2 text-right text-green-600">{formatCurrency(datos?.resumen?.importe_total)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        /* Lista de minutas */
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Lista de Minutas Completadas - {anno}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Nº Minuta</th>
                    <th className="px-4 py-2 text-left">Expediente</th>
                    <th className="px-4 py-2 text-left">Tipo Costas</th>
                    <th className="px-4 py-2 text-left">Procurador</th>
                    <th className="px-4 py-2 text-left">Fecha</th>
                    <th className="px-4 py-2 text-right">Base</th>
                    <th className="px-4 py-2 text-right">IVA</th>
                    <th className="px-4 py-2 text-right">Total</th>
                    {isAdmin && <th className="px-4 py-2 text-center">Acciones</th>}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {minutasFiltradas?.length > 0 ? (
                    minutasFiltradas.map((minuta) => (
                      <tr key={minuta.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 font-mono font-bold text-blue-600">{minuta.numero_minuta}</td>
                        <td className="px-4 py-2">{minuta.expediente_numero || '-'}</td>
                        <td className="px-4 py-2 text-xs">
                          <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded">{minuta.tipo_costas}</span>
                        </td>
                        <td className="px-4 py-2">{minuta.procurador_nombre || '-'}</td>
                        <td className="px-4 py-2">{formatDate(minuta.fecha_completada)}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(minuta.base_imponible)}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(minuta.iva)}</td>
                        <td className="px-4 py-2 text-right font-medium text-green-600">{formatCurrency(minuta.importe_total)}</td>
                        {isAdmin && (
                          <td className="px-4 py-2 text-center">
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteMinuta(minuta)} title="Eliminar minuta" data-testid="delete-minuta-contabilidad-btn">
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={isAdmin ? 9 : 8} className="px-4 py-12 text-center text-gray-500">
                        <DollarSign className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        No hay minutas completadas en {anno}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};


const Contabilidad = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'facturas');
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [filterFacturador, setFilterFacturador] = useState(''); // '' = todos | <facturador.id>
  // [v136] actividad: '' = todas | profesional | arrendamiento
  const [filterActividad, setFilterActividad] = useState('');
  const [filterAnno, setFilterAnno] = useState(String(new Date().getFullYear())); // 'all' = todos los años
  const [ejercicios, setEjercicios] = useState([]);
  
  // Dialog
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingFactura, setEditingFactura] = useState(null);
  
  // Contactos y Expedientes para selects
  const [contactos, setContactos] = useState([]);
  const [expedientes, setExpedientes] = useState([]);
  const [facturadores, setFacturadores] = useState([]);
  // [v137] fichas de inmuebles, para asignar la renta a su inmueble
  const [inmuebles, setInmuebles] = useState([]);
  const [selectedClienteData, setSelectedClienteData] = useState(null);
  const [selectedExpedienteData, setSelectedExpedienteData] = useState(null);
  const previewTimeoutRef = React.useRef(null);
  
  // Stats
  const [stats, setStats] = useState({
    total_pendiente: 0,
    total_pagado: 0,
    total_facturas: 0,
    facturas_vencidas: 0
  });
  
  // Form
  const [form, setForm] = useState({
    tipo: 'proforma',
    // [v136] actividad: profesional (honorarios) | arrendamiento (alquiler de inmuebles)
    origen: 'profesional',
    inmueble_id: '',
    cliente_id: '',
    expediente_id: '',
    facturador_id: '',
    numero_factura: '',
    descripcion: '',
    porcentaje_iva: 21,
    porcentaje_retencion: 15,
    fecha_emision: new Date().toISOString().split('T')[0],
    fecha_vencimiento: '',
    forma_pago: 'TRANSFERENCIA BANCARIA',
    notas: '',
    conceptos: [{ concepto: '', descripcion: '', unidades: 1, precio: 0, base_imponible: 0 }],
    cotitulares: []  // co-titulares informativos (no fiscales)
  });
  const [numeroManual, setNumeroManual] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    loadData();
    // Recarga el listado y los totales al cambiar de ejercicio fiscal
  }, [filterAnno]);

  const loadData = async () => {
    try {
      setLoading(true);
      const paramsAnno = filterAnno && filterAnno !== 'all' ? { ejercicio: Number(filterAnno) } : {};
      const [facturasRes, contactosRes, expedientesRes, statsRes, facturadoresRes, ejerciciosRes] = await Promise.all([
        facturasAPI.getAll({ ...paramsAnno, limit: 5000 }),
        contactosAPI.getAll(),
        expedientesAPI.getAll(),
        facturasAPI.getStats(paramsAnno),
        configuracionAPI.getFacturadores().catch(() => ({ data: [] })),
        facturasAPI.getEjercicios().catch(() => ({ data: { ejercicios: [] } }))
      ]);
      
      setEjercicios(Array.isArray(ejerciciosRes.data?.ejercicios) ? ejerciciosRes.data.ejercicios : []);
      
      // Manejar diferentes formatos de respuesta
      let facturasData = facturasRes.data;
      if (facturasData && facturasData.data) {
        facturasData = facturasData.data;
      }
      setFacturas(Array.isArray(facturasData) ? facturasData : []);
      
      const contactosList = contactosRes.data?.data || contactosRes.data || [];
      // No filtrar por tipo='cliente': cualquier contacto (persona, empresa u organismo)
      // puede ser cliente de una factura. El filtrado legacy excluía empresas.
      setContactos(Array.isArray(contactosList) ? contactosList : []);
      
      let expedientesData = expedientesRes.data;
      if (expedientesData && expedientesData.data) {
        expedientesData = expedientesData.data;
      }
      setExpedientes(Array.isArray(expedientesData) ? expedientesData : []);
      
      setStats(statsRes.data || {});
      setFacturadores(Array.isArray(facturadoresRes.data) ? facturadoresRes.data : []);
      try {
        const fichas = await inmueblesAPI.listar();
        setInmuebles(fichas.inmuebles || []);
      } catch (e) {
        setInmuebles([]);
      }
    } catch (error) {
      console.error('Error cargando datos:', error);
      toast({ title: "Error", description: "No se pudieron cargar los datos", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const fetchPreviewNumero = async (facturadorId, tipo, origen = 'profesional') => {
    if (!facturadorId) {
      setForm(prev => ({ ...prev, numero_factura: '' }));
      return;
    }
    try {
      setPreviewLoading(true);
      const res = await facturasAPI.previewNumero({ facturador_id: facturadorId, tipo, origen });
      const numero = res.data?.numero || '';
      setForm(prev => ({ ...prev, numero_factura: numero }));
    } catch (e) {
      console.error('Error preview numero:', e);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Auto-preview al cambiar facturador o tipo
  useEffect(() => {
    if (!isDialogOpen) return;
    if (numeroManual) return;
    if (!form.facturador_id) return;
    if (previewTimeoutRef.current) clearTimeout(previewTimeoutRef.current);
    previewTimeoutRef.current = setTimeout(() => {
      fetchPreviewNumero(form.facturador_id, form.tipo, form.origen);
    }, 200);
    // eslint-disable-next-line
  }, [form.facturador_id, form.tipo, form.origen, isDialogOpen]);

  const resetForm = () => {
    setForm({
      tipo: 'proforma',
      origen: 'profesional',
      inmueble_id: '',
      cliente_id: '',
      expediente_id: '',
      facturador_id: '',
      numero_factura: '',
      descripcion: '',
      porcentaje_iva: 21,
      porcentaje_retencion: 15,
      fecha_emision: new Date().toISOString().split('T')[0],
      fecha_vencimiento: '',
      forma_pago: 'TRANSFERENCIA BANCARIA',
      notas: '',
      conceptos: [{ concepto: '', descripcion: '', unidades: 1, precio: 0, base_imponible: 0 }],
      cotitulares: []
    });
    setNumeroManual(false);
    setEditingFactura(null);
  };

  const handleEdit = (factura) => {
    // Navegar a detalle para editar
    navigate(`/contabilidad/factura/${factura.id}`);
  };

  const addConcepto = () => {
    setForm({
      ...form,
      conceptos: [...form.conceptos, { concepto: '', descripcion: '', unidades: 1, precio: 0, base_imponible: 0 }]
    });
  };

  const updateConcepto = (index, field, value) => {
    const newConceptos = [...form.conceptos];
    newConceptos[index] = { ...newConceptos[index], [field]: value };
    
    if (field === 'unidades' || field === 'precio') {
      const unidades = field === 'unidades' ? parseFloat(value) : parseFloat(newConceptos[index].unidades);
      const precio = field === 'precio' ? parseFloat(value) : parseFloat(newConceptos[index].precio);
      newConceptos[index].base_imponible = unidades * precio;
    }
    
    setForm({ ...form, conceptos: newConceptos });
  };

  const removeConcepto = (index) => {
    if (form.conceptos.length > 1) {
      setForm({ ...form, conceptos: form.conceptos.filter((_, i) => i !== index) });
    }
  };

  const calcularTotal = () => {
    const base = form.conceptos.reduce((sum, c) => sum + (c.base_imponible || 0), 0);
    const iva = base * (form.porcentaje_iva / 100);
    const retencion = base * (form.porcentaje_retencion / 100);
    return { base, iva, retencion, total: base + iva - retencion };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const totales = calcularTotal();
      const data = {
        tipo: form.tipo,
        origen: form.origen,
        inmueble_id: form.origen === 'arrendamiento' && form.inmueble_id
          ? Number(form.inmueble_id) : null,
        cliente_id: form.cliente_id ? parseInt(form.cliente_id) : null,
        expediente_id: form.expediente_id ? parseInt(form.expediente_id) : null,
        facturador_id: form.facturador_id ? parseInt(form.facturador_id) : null,
        numero_factura: form.numero_factura ? form.numero_factura.trim() : null,
        descripcion: form.descripcion,
        porcentaje_iva: parseFloat(form.porcentaje_iva),
        porcentaje_retencion: parseFloat(form.porcentaje_retencion),
        fecha_emision: form.fecha_emision,
        fecha_vencimiento: form.fecha_vencimiento || null,
        forma_pago: form.forma_pago,
        notas: form.notas,
        conceptos: form.conceptos.map(c => ({
          ...c,
          impuesto_pct: form.porcentaje_iva,
          retencion_pct: form.porcentaje_retencion
        })),
        suplidos: [],
        cotitulares_ids: (form.cotitulares || []).map(c => c.id).filter(Boolean)
      };
      
      await facturasAPI.create(data);
      toast({ title: "Factura creada" });
      setIsDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      let msg = "No se pudo guardar";
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail)) {
        msg = detail.map(e => `${(e.loc || []).slice(-1)[0] || "campo"}: ${e.msg || JSON.stringify(e)}`).join(" · ");
      } else if (detail && typeof detail === "object") {
        msg = detail.msg || JSON.stringify(detail);
      }
      toast({ 
        title: "Error", 
        description: msg,
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (facturaId) => {
    if (!window.confirm('¿Eliminar esta factura?')) return;
    
    try {
      await facturasAPI.delete(facturaId);
      toast({ title: "Factura eliminada" });
      loadData();
    } catch (error) {
      toast({ title: "Error", description: "No se pudo eliminar", variant: "destructive" });
    }
  };

  const handleMarcarPagada = async (facturaId) => {
    try {
      await facturasAPI.marcarPagada(facturaId);
      toast({ title: "Factura marcada como pagada" });
      loadData();
    } catch (error) {
      toast({ title: "Error", variant: "destructive" });
    }
  };

  // Filtrar facturas - búsqueda por cualquier campo
  // Mapeo factura → facturador.id (vía sufijo del nº ó vía serie === nombre)
  const getFacturadorIdOfFactura = React.useCallback((f) => {
    if (!f || !facturadores || facturadores.length === 0) return null;
    const num = (f.numero_factura || '').toString();
    const parts = num.split('-');
    if (parts.length >= 2) {
      const suffix = parts[parts.length - 1].trim().toUpperCase();
      if (suffix) {
        const m = facturadores.find(fc => ((fc.prefijo_serie || '').trim().toUpperCase()) === suffix);
        if (m) return m.id;
      }
    }
    if (f.serie) {
      const m = facturadores.find(fc => (fc.nombre || '').trim() === (f.serie || '').trim());
      if (m) return m.id;
    }
    return null;
  }, [facturadores]);

  const filteredFacturas = facturas.filter(f => {
    const searchLower = search.toLowerCase();
    const matchSearch = !search || 
      f.numero_factura?.toLowerCase().includes(searchLower) ||
      f.cliente_nombre?.toLowerCase().includes(searchLower) ||
      f.cliente_nif?.toLowerCase().includes(searchLower) ||
      f.descripcion?.toLowerCase().includes(searchLower) ||
      f.codigo_expediente?.toLowerCase().includes(searchLower) ||
      f.notas?.toLowerCase().includes(searchLower) ||
      formatCurrency(f.total).toLowerCase().includes(searchLower) ||
      formatDate(f.fecha_emision).includes(search) ||
      f.forma_pago?.toLowerCase().includes(searchLower);
    const matchEstado = !filterEstado || f.estado === filterEstado;
    const matchTipo = !filterTipo || f.tipo === filterTipo;
    const matchFacturador = !filterFacturador
      || (filterFacturador === 'sin' && getFacturadorIdOfFactura(f) === null)
      || (String(getFacturadorIdOfFactura(f)) === String(filterFacturador));
    const actividad = f.origen === 'arrendamiento' ? 'arrendamiento' : 'profesional';
    const matchActividad = !filterActividad || actividad === filterActividad;
    return matchSearch && matchEstado && matchTipo && matchFacturador && matchActividad;
  });

  // Stats por facturador (cliente-side, sobre todas las facturas cargadas).
  // Útil cuando el despacho usa varios facturadores (LEXDUO + EC).
  const statsPorFacturador = useMemo(() => {
    const map = {};
    const ensure = (key, label) => {
      if (!map[key]) {
        map[key] = {
          key,
          label,
          total_emitido: 0,
          total_pagado: 0,
          total_pendiente: 0,
          facturas: 0,
          vencidas: 0,
        };
      }
      return map[key];
    };
    const hoy = new Date();
    facturas.forEach(f => {
      if (f.estado === 'anulada') return;
      // Sólo contamos documentos tipo 'factura' o 'emitida' (no proformas ni presupuestos)
      const tipo = (f.tipo || '').toLowerCase();
      if (tipo !== 'factura' && tipo !== 'emitida' && tipo !== 'rectificativa') return;
      const fid = getFacturadorIdOfFactura(f);
      const fac = fid ? facturadores.find(x => x.id === fid) : null;
      const key = fid ? String(fid) : 'sin';
      const label = fac
        ? (fac.prefijo_serie || fac.nombre || `Facturador ${fid}`)
        : 'Sin facturador';
      const bucket = ensure(key, label);
      const total = parseFloat(f.total) || 0;
      const pagado = parseFloat(f.total_pagado) || 0;
      bucket.total_emitido += total;
      bucket.total_pagado += pagado;
      bucket.total_pendiente += Math.max(0, total - pagado);
      bucket.facturas += 1;
      if (f.fecha_vencimiento && new Date(f.fecha_vencimiento) < hoy && pagado < total) {
        bucket.vencidas += 1;
      }
    });
    // Orden: primero facturadores reales (por total emitido desc), "sin" al final.
    const arr = Object.values(map);
    arr.sort((a, b) => {
      if (a.key === 'sin' && b.key !== 'sin') return 1;
      if (b.key === 'sin' && a.key !== 'sin') return -1;
      return b.total_emitido - a.total_emitido;
    });
    return arr;
  }, [facturas, facturadores, getFacturadorIdOfFactura]);

  const getEstadoBadge = (estado) => {
    const est = ESTADOS_FACTURA.find(e => e.value === estado);
    return est || ESTADOS_FACTURA[0];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <DollarSign className="w-6 h-6" /> Contabilidad
          </h1>
          <p className="text-gray-600">Gestión de facturas, presupuestos y pagos</p>
        </div>
        <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" /> Nueva factura
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total facturado{filterAnno === 'all' ? ' · todos los años' : ` · ${filterAnno}`}</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.total_pagado + stats.total_pendiente)}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Receipt className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Cobrado</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.total_pagado)}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pendiente</p>
                <p className="text-2xl font-bold text-yellow-600">{formatCurrency(stats.total_pendiente)}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Facturas vencidas</p>
                <p className="text-2xl font-bold text-red-600">{stats.facturas_vencidas || 0}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Total facturado · Desglose por facturador */}
      {statsPorFacturador.length > 1 && (
        <Card data-testid="stats-por-facturador">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Building className="w-4 h-4" /> Total facturado · por facturador
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {statsPorFacturador.map(s => (
                <div
                  key={s.key}
                  className={`p-4 rounded-lg border transition-colors cursor-pointer ${
                    String(filterFacturador) === String(s.key)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                  }`}
                  onClick={() => setFilterFacturador(String(filterFacturador) === String(s.key) ? '' : s.key)}
                  data-testid={`facturador-card-${s.key}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-900">{s.label}</span>
                    <Badge variant="outline" className="text-xs">{s.facturas} factura{s.facturas !== 1 ? 's' : ''}</Badge>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Emitido</span>
                      <span className="font-bold text-gray-900">{formatCurrency(s.total_emitido)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Cobrado</span>
                      <span className="font-medium text-green-600">{formatCurrency(s.total_pagado)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Pendiente</span>
                      <span className="font-medium text-yellow-600">{formatCurrency(s.total_pendiente)}</span>
                    </div>
                    {s.vencidas > 0 && (
                      <div className="flex justify-between pt-1 border-t border-gray-200">
                        <span className="text-red-500">Vencidas</span>
                        <span className="font-medium text-red-600">{s.vencidas}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {filterFacturador && (
              <div className="mt-3 text-xs text-gray-500 italic">
                Click en una tarjeta para alternar el filtro · Filtro activo: <strong>{statsPorFacturador.find(s => String(s.key) === String(filterFacturador))?.label || ''}</strong>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="facturas" className="flex items-center gap-2">
            <FileText className="w-4 h-4" /> Facturas
          </TabsTrigger>
          <TabsTrigger value="minutas" className="flex items-center gap-2">
            <DollarSign className="w-4 h-4" /> Minutas
          </TabsTrigger>
          <TabsTrigger value="gastos" className="flex items-center gap-2">
            <Receipt className="w-4 h-4" /> Controles de Gastos
          </TabsTrigger>
          <TabsTrigger value="morosidad" className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Morosidad
          </TabsTrigger>
          <TabsTrigger value="presupuestos" className="flex items-center gap-2">
            <PiggyBank className="w-4 h-4" /> Presupuestos
          </TabsTrigger>
          <TabsTrigger value="informes" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" /> Informes
          </TabsTrigger>
          <TabsTrigger value="honorarios" className="flex items-center gap-2">
            <Percent className="w-4 h-4" /> Honorarios
          </TabsTrigger>
          <TabsTrigger value="fiscalidad" className="flex items-center gap-2" data-testid="tab-fiscalidad">
            <Landmark className="w-4 h-4" /> Fiscalidad
          </TabsTrigger>
          <TabsTrigger value="__pago_tasas_nav__" className="flex items-center gap-2 data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700" onClick={(e) => { e.preventDefault(); window.location.href = '/expedientes/pago-tasas'; }} data-testid="tab-pago-tasas-contabilidad">
            <Receipt className="w-4 h-4" /> Pago de Tasas
          </TabsTrigger>
        </TabsList>

        <TabsContent value="facturas" className="mt-4">
          {/* Filtros */}
          <Card className="mb-4">
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input 
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Buscar por número, concepto o cliente..."
                      className="pl-10"
                    />
                  </div>
                </div>
                <Select value={filterAnno} onValueChange={setFilterAnno}>
                  <SelectTrigger className="w-[170px]" data-testid="filter-anno-trigger">
                    <SelectValue placeholder="Año fiscal" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" data-testid="filter-anno-todos">Todos los años</SelectItem>
                    {(ejercicios.length > 0
                      ? ejercicios.map(e => e.anno)
                      : [new Date().getFullYear()]
                    ).map(a => (
                      <SelectItem key={a} value={String(a)} data-testid={`filter-anno-${a}`}>
                        Año {a}
                      </SelectItem>
                    ))}
                    {ejercicios.length > 0 && !ejercicios.some(e => String(e.anno) === String(new Date().getFullYear())) && (
                      <SelectItem value={String(new Date().getFullYear())}>
                        Año {new Date().getFullYear()}
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                <Select value={filterEstado || 'all'} onValueChange={(v) => setFilterEstado(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {ESTADOS_FACTURA.map(e => (
                      <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterTipo || 'all'} onValueChange={(v) => setFilterTipo(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {TIPOS_FILTRO.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterFacturador || 'all'} onValueChange={(v) => setFilterFacturador(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-[180px]" data-testid="filter-facturador-trigger">
                    <SelectValue placeholder="Facturador" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los facturadores</SelectItem>
                    {facturadores.map(fc => (
                      <SelectItem key={fc.id} value={String(fc.id)}>
                        {fc.prefijo_serie ? `${fc.prefijo_serie} · ${fc.nombre}` : fc.nombre}
                      </SelectItem>
                    ))}
                    <SelectItem value="sin">Sin facturador</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterActividad || 'all'}
                        onValueChange={(v) => setFilterActividad(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-[190px]" data-testid="filter-actividad-trigger">
                    <SelectValue placeholder="Actividad" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las actividades</SelectItem>
                    <SelectItem value="profesional">Honorarios profesionales</SelectItem>
                    <SelectItem value="arrendamiento">Arrendamiento de inmuebles</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Tabla */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Número</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Concepto</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Estado</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Fiscal</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredFacturas.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                          No se encontraron facturas
                        </td>
                      </tr>
                    ) : (
                      filteredFacturas.map(factura => {
                        const estadoBadge = getEstadoBadge(factura.estado);
                        const EstadoIcon = estadoBadge.icon;
                        return (
                          <tr 
                            key={factura.id} 
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => navigate(`/contabilidad/factura/${factura.id}`)}
                          >
                            <td className="px-4 py-3">
                              <span className="font-medium text-blue-600">{factura.numero_factura || `FAC-${factura.id}`}</span>
                              <div className="text-xs text-gray-500 capitalize">{factura.tipo}</div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="font-medium">{factura.cliente_nombre || '-'}</div>
                              {factura.codigo_expediente && (
                                <div className="text-xs text-gray-500">Exp: {factura.codigo_expediente}</div>
                              )}
                              {factura.origen === 'arrendamiento' && (
                                <div className="text-xs text-emerald-700 font-medium"
                                     data-testid={`factura-actividad-${factura.id}`}>
                                  Arrendamiento
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 max-w-[200px] truncate">{factura.descripcion || '-'}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              <div>{formatDate(factura.fecha_emision)}</div>
                              {factura.fecha_vencimiento && (
                                <div className="text-xs text-gray-400">Vence: {formatDate(factura.fecha_vencimiento)}</div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="font-semibold">{formatCurrency(factura.total)}</span>
                              {factura.porcentaje_retencion > 0 && (
                                <div className="text-xs text-red-500">IRPF -{factura.porcentaje_retencion}%</div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <Badge className={estadoBadge.color}>
                                <EstadoIcon className="w-3 h-3 mr-1" />
                                {estadoBadge.label}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <FiscalStateBadge fiscalState={factura.fiscal_state} />
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => navigate(`/contabilidad/factura/${factura.id}`)}
                                  title="Ver detalle"
                                >
                                  <Eye className="w-4 h-4 text-blue-600" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleEdit(factura)}>
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDelete(factura.id)}>
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

                {/* Tab de Minutas */}
        <TabsContent value="minutas" className="mt-4">
          <MinutasContabilidadTab />
        </TabsContent>

        <TabsContent value="gastos" className="mt-4">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600">
                Gestión de gastos del despacho. Registra las facturas de proveedores.
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={() => navigate('/contabilidad/gastos/estadisticas')}
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Ver Estadísticas
                </Button>
                <Button onClick={() => navigate('/contabilidad/gastos')}>
                  <Receipt className="w-4 h-4 mr-2" />
                  Ir a Controles de Gastos
                </Button>
              </div>
            </div>
            <Card>
              <CardContent className="py-12 text-center">
                <Receipt className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900">Controles de Gastos</h3>
                <p className="text-gray-500 mt-2">
                  Lleva el control de las facturas de proveedores y gastos del despacho.
                </p>
                <p className="text-gray-500 mt-1">
                  Exporta datos, genera informes y visualiza estadísticas.
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="morosidad" className="mt-4">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600">
                Control de clientes morosos e impagos de facturas.
              </p>
              <Button onClick={() => navigate('/contabilidad/morosidad')}>
                <AlertTriangle className="w-4 h-4 mr-2" />
                Ir a Control de Morosidad
              </Button>
            </div>
            <Card>
              <CardContent className="py-12 text-center">
                <AlertTriangle className="w-16 h-16 text-red-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900">Control de Morosidad</h3>
                <p className="text-gray-500 mt-2">
                  Gestiona clientes con impagos, registra actuaciones y seguimiento de cobros.
                </p>
                <p className="text-gray-500 mt-1">
                  Incluye gestión de demandas judiciales y archivos adjuntos.
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="presupuestos" className="mt-4">
          <Card>
            <CardContent className="py-12 text-center">
              <PiggyBank className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900">Gestión de Presupuestos</h3>
              <p className="text-gray-500 mt-2">Crea presupuestos y conviértelos en facturas cuando sean aceptados</p>
              <Button 
                className="mt-4"
                onClick={() => { resetForm(); setForm(prev => ({...prev, tipo: 'presupuesto'})); setIsDialogOpen(true); }}
              >
                <Plus className="w-4 h-4 mr-2" /> Nuevo presupuesto
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="informes" className="mt-4">
          <InformesContabilidad facturas={facturas} contactos={contactos} facturadores={facturadores} />
        </TabsContent>

        <TabsContent value="honorarios" className="mt-4">
          <HonorariosTab />
        </TabsContent>

        <TabsContent value="fiscalidad" className="mt-4">
          <FiscalidadTab />
        </TabsContent>
      </Tabs>

      {/* Dialog crear/editar */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingFactura ? 'Editar' : 'Nueva'} {form.tipo}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label>Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({...form, tipo: v})}>
                  <SelectTrigger data-testid="factura-tipo-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPOS_FACTURA.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Facturador *</Label>
                <Select value={form.facturador_id ? String(form.facturador_id) : ''} onValueChange={(v) => { setNumeroManual(false); setForm({...form, facturador_id: v}); }}>
                  <SelectTrigger data-testid="factura-facturador-trigger">
                    <SelectValue placeholder="Selecciona facturador..." />
                  </SelectTrigger>
                  <SelectContent>
                    {facturadores.length === 0 && (
                      <div className="p-2 text-sm text-gray-500">
                        No hay facturadores. Créalos en Configuración → Facturadores.
                      </div>
                    )}
                    {facturadores.map(f => (
                      <SelectItem key={f.id} value={String(f.id)}>
                        {f.nombre}{f.prefijo_serie ? ` (${f.prefijo_serie})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(() => {
                  const fSel = facturadores.find(f => String(f.id) === String(form.facturador_id));
                  if (!fSel || !fSel.campos_incompletos || fSel.campos_incompletos.length === 0) return null;
                  return (
                    <p
                      className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-2"
                      data-testid="factura-facturador-incompleto"
                    >
                      <AlertTriangle className="w-3 h-3 inline mr-1" />
                      A este facturador le faltan datos fiscales ({fSel.campos_incompletos.join(', ')}).
                      Puedes seguir, pero complétalos en Configuración → Facturadores.
                    </p>
                  );
                })()}
              </div>
              <div>
                <Label>Actividad</Label>
                <Select
                  value={form.origen}
                  onValueChange={(v) => {
                    setNumeroManual(false);
                    setForm((prev) => ({
                      ...prev,
                      origen: v,
                      // El alquiler de local va al 21 % y la retención del 19 % se decide
                      // factura a factura según el arrendatario; los honorarios siguen al 15 %.
                      porcentaje_retencion: v === 'arrendamiento' ? 19 : 15,
                    }));
                  }}
                >
                  <SelectTrigger data-testid="factura-actividad-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="profesional">Honorarios profesionales</SelectItem>
                    <SelectItem value="arrendamiento">Arrendamiento de inmuebles</SelectItem>
                  </SelectContent>
                </Select>
                {form.origen === 'arrendamiento' && inmuebles.length > 0 && (
                  <div className="mt-2">
                    <Label className="text-xs">Inmueble</Label>
                    <Select value={form.inmueble_id ? String(form.inmueble_id) : ''}
                            onValueChange={(v) => setForm({ ...form, inmueble_id: v })}>
                      <SelectTrigger data-testid="factura-inmueble-trigger">
                        <SelectValue placeholder="¿De qué inmueble es la renta?" />
                      </SelectTrigger>
                      <SelectContent>
                        {inmuebles.map((i) => (
                          <SelectItem key={i.id} value={String(i.id)}>{i.direccion}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {form.origen === 'arrendamiento' && (() => {
                  const fSel = facturadores.find((f) => String(f.id) === String(form.facturador_id));
                  if (form.facturador_id && fSel && !fSel.arrendamientos_activo) {
                    return (
                      <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mt-2"
                         data-testid="factura-arrendamiento-no-activo">
                        <AlertTriangle className="w-3 h-3 inline mr-1" />
                        {fSel.nombre} no tiene activada la actividad de arrendamiento. Actívala en
                        Configuración → Facturadores.
                      </p>
                    );
                  }
                  return (
                    <p className="text-xs text-emerald-700 mt-1" data-testid="factura-arrendamiento-nota">
                      Serie propia de alquileres. No entra en el modelo 130; la retención del 19 %
                      la practica el arrendatario cuando es empresa, profesional o entidad.
                    </p>
                  );
                })()}
              </div>
              <div>
                <Label>Forma de pago</Label>
                <Select value={form.forma_pago} onValueChange={(v) => setForm({...form, forma_pago: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TRANSFERENCIA BANCARIA">Transferencia bancaria</SelectItem>
                    <SelectItem value="EFECTIVO">Efectivo</SelectItem>
                    <SelectItem value="TARJETA">Tarjeta</SelectItem>
                    <SelectItem value="BIZUM">Bizum</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="flex items-center justify-between">
                  <span>Nº Factura</span>
                  {previewLoading && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
                </Label>
                <Input
                  value={form.numero_factura}
                  onChange={(e) => { setNumeroManual(true); setForm({...form, numero_factura: e.target.value}); }}
                  placeholder={form.facturador_id ? 'Auto...' : 'Selecciona facturador'}
                  data-testid="factura-numero-input"
                  className={numeroManual ? 'border-blue-500' : ''}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {numeroManual ? 'Editado manualmente' : 'Se genera automáticamente'}
                </p>
              </div>
              <div className="col-span-2 grid grid-cols-2 gap-4">
                <div>
                  <Label>Cliente *</Label>
                  <ClienteCombobox
                    value={form.cliente_id}
                    onChange={(v) => setForm({...form, cliente_id: v})}
                    contactos={contactos}
                    onSelectedChange={setSelectedClienteData}
                  />
                  {selectedClienteData && (
                    <div className="mt-2 p-3 rounded-md border border-green-200 bg-green-50 text-xs space-y-0.5" data-testid="cliente-datos-card">
                      <div className="font-semibold text-green-900">
                        {selectedClienteData.nombre_completo || selectedClienteData.nombre}
                      </div>
                      {selectedClienteData.nif_cif && (
                        <div className="text-green-800">NIF/CIF: <span className="font-mono">{selectedClienteData.nif_cif}</span></div>
                      )}
                      {selectedClienteData.email && (
                        <div className="text-green-800 truncate">{selectedClienteData.email}</div>
                      )}
                      {(selectedClienteData.telefono || selectedClienteData.telefono_movil) && (
                        <div className="text-green-800">Tel: {selectedClienteData.telefono || selectedClienteData.telefono_movil}</div>
                      )}
                      {selectedClienteData.direccion && (
                        <div className="text-green-800 truncate">
                          {selectedClienteData.direccion}{selectedClienteData.codigo_postal ? `, ${selectedClienteData.codigo_postal}` : ''}{selectedClienteData.ciudad ? ` ${selectedClienteData.ciudad}` : ''}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <Label>Expediente (opcional)</Label>
                  <ExpedienteComboboxFacturas
                    value={form.expediente_id}
                    onChange={(v, expObj) => {
                      // Si seleccionamos un expediente y no hay cliente todavía,
                      // autocompletar cliente_id con el cliente del expediente.
                      const exp = expObj || expedientes.find(e => String(e.id) === String(v));
                      setForm(prev => ({
                        ...prev,
                        expediente_id: v,
                        cliente_id: (exp && exp.cliente_id && !prev.cliente_id)
                          ? String(exp.cliente_id)
                          : prev.cliente_id,
                      }));
                    }}
                    expedientes={expedientes}
                    onSelectedChange={setSelectedExpedienteData}
                  />
                  {selectedExpedienteData && (
                    <div className="mt-2 p-3 rounded-md border border-green-200 bg-green-50 text-xs space-y-0.5" data-testid="expediente-datos-card">
                      <div className="font-semibold text-green-900 truncate">
                        {selectedExpedienteData.numero_expediente} — {selectedExpedienteData.titulo}
                      </div>
                      {selectedExpedienteData.cliente_nombre && (
                        <div className="text-green-800">Cliente: {selectedExpedienteData.cliente_nombre}</div>
                      )}
                      {selectedExpedienteData.tipo_procedimiento && (
                        <div className="text-green-800">Procedimiento: {selectedExpedienteData.tipo_procedimiento}</div>
                      )}
                      {selectedExpedienteData.estado && (
                        <div className="text-green-800">Estado: {selectedExpedienteData.estado}</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Co-titulares informativos */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <CotitularesSelector
                value={form.cotitulares}
                onChange={(list) => setForm({...form, cotitulares: list})}
                excludeIds={form.cliente_id ? [form.cliente_id] : []}
              />
            </div>

            <div>
              <Label>Descripción</Label>
              <Input
                value={form.descripcion}
                onChange={(e) => setForm({...form, descripcion: e.target.value})}
                placeholder="Ej: SERVICIOS JURIDICOS"
              />
            </div>

            {/* Conceptos facturables */}
            <div className="border rounded-lg p-4">
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-medium text-sm">Conceptos facturables</h4>
                <Button type="button" size="sm" variant="outline" onClick={addConcepto}>
                  <Plus className="w-4 h-4 mr-1" /> Añadir concepto
                </Button>
              </div>
              {form.conceptos.map((c, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-end">
                  <div className="col-span-3">
                    {i === 0 && <Label className="text-xs text-gray-500">Concepto</Label>}
                    <Input
                      placeholder="Concepto"
                      value={c.concepto}
                      onChange={(e) => updateConcepto(i, 'concepto', e.target.value)}
                    />
                  </div>
                  <div className="col-span-3">
                    {i === 0 && <Label className="text-xs text-gray-500">Descripción</Label>}
                    <Input
                      placeholder="Descripción"
                      value={c.descripcion}
                      onChange={(e) => updateConcepto(i, 'descripcion', e.target.value)}
                    />
                  </div>
                  <div className="col-span-1">
                    {i === 0 && <Label className="text-xs text-gray-500">Uds.</Label>}
                    <Input
                      type="number"
                      step="0.01"
                      value={c.unidades}
                      onChange={(e) => updateConcepto(i, 'unidades', e.target.value)}
                    />
                  </div>
                  <div className="col-span-2">
                    {i === 0 && <Label className="text-xs text-gray-500">Precio</Label>}
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={c.precio}
                      onChange={(e) => updateConcepto(i, 'precio', e.target.value)}
                    />
                  </div>
                  <div className="col-span-2">
                    {i === 0 && <Label className="text-xs text-gray-500">Base</Label>}
                    <Input
                      type="number"
                      readOnly
                      value={(c.base_imponible || (parseFloat(c.unidades) || 0) * (parseFloat(c.precio) || 0)).toFixed(2)}
                      className="bg-gray-50"
                    />
                  </div>
                  <div className="col-span-1">
                    {i === 0 && <Label className="text-xs text-gray-500">&nbsp;</Label>}
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeConcepto(i)} disabled={form.conceptos.length <= 1}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* IVA y Retención IRPF */}
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label>% IVA</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.porcentaje_iva}
                  onChange={(e) => setForm({...form, porcentaje_iva: e.target.value})}
                />
              </div>
              <div>
                <Label>% Retención IRPF</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.porcentaje_retencion}
                  onChange={(e) => setForm({...form, porcentaje_retencion: e.target.value})}
                />
              </div>
              <div className="col-span-2">
                <Label>Fecha emisión</Label>
                <Input 
                  type="date"
                  value={form.fecha_emision}
                  onChange={(e) => setForm({...form, fecha_emision: e.target.value})}
                />
              </div>
            </div>

            {/* Resumen de totales calculados */}
            {(() => {
              const totales = calcularTotal();
              return (
                <div className="bg-gray-50 rounded-lg p-4 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span>Base Imponible</span>
                    <span>{formatCurrency(totales.base)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>IVA ({form.porcentaje_iva}%)</span>
                    <span>{formatCurrency(totales.iva)}</span>
                  </div>
                  {parseFloat(form.porcentaje_retencion) > 0 && (
                    <div className="flex justify-between text-sm text-red-600">
                      <span>Retención IRPF ({form.porcentaje_retencion}%)</span>
                      <span>-{formatCurrency(totales.retencion)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg border-t pt-2 mt-2">
                    <span>TOTAL</span>
                    <span>{formatCurrency(totales.total)}</span>
                  </div>
                </div>
              );
            })()}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fecha vencimiento</Label>
                <Input 
                  type="date"
                  value={form.fecha_vencimiento}
                  onChange={(e) => setForm({...form, fecha_vencimiento: e.target.value})}
                />
              </div>
              <div>
                <Label>Observaciones</Label>
                <Textarea 
                  value={form.notas}
                  onChange={(e) => setForm({...form, notas: e.target.value})}
                  placeholder="Notas adicionales..."
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Guardar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Constantes para gráficos
const CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];
const MESES_CHART = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

// Componente de Informes de Contabilidad con gráficos
const InformesContabilidad = ({ facturas = [], contactos = [], facturadores = [] }) => {
  
  // Asegurar que son arrays
  const facturasArray = Array.isArray(facturas) ? facturas : [];
  const contactosArray = Array.isArray(contactos) ? contactos : [];
  const facturadoresArray = Array.isArray(facturadores) ? facturadores : [];

  // Helper: factura → facturador.id (mismo criterio que en la lista)
  const getFacturadorIdOfFactura = (f) => {
    if (!f || facturadoresArray.length === 0) return null;
    const num = (f.numero_factura || '').toString();
    const parts = num.split('-');
    if (parts.length >= 2) {
      const suffix = parts[parts.length - 1].trim().toUpperCase();
      if (suffix) {
        const m = facturadoresArray.find(fc => ((fc.prefijo_serie || '').trim().toUpperCase()) === suffix);
        if (m) return m.id;
      }
    }
    if (f.serie) {
      const m = facturadoresArray.find(fc => (fc.nombre || '').trim() === (f.serie || '').trim());
      if (m) return m.id;
    }
    return null;
  };

  // Agregado por facturador (todas las facturas, todos los años)
  const porFacturador = useMemo(() => {
    const map = {};
    facturasArray.forEach(f => {
      if (f.estado === 'anulada') return;
      const tipo = (f.tipo || '').toLowerCase();
      if (tipo !== 'factura' && tipo !== 'emitida' && tipo !== 'rectificativa') return;
      const fid = getFacturadorIdOfFactura(f);
      const fac = fid ? facturadoresArray.find(x => x.id === fid) : null;
      const key = fid ? String(fid) : 'sin';
      const label = fac
        ? (fac.prefijo_serie || fac.nombre || `Facturador ${fid}`)
        : 'Sin facturador';
      if (!map[key]) {
        map[key] = { key, label, emitido: 0, cobrado: 0, pendiente: 0, facturas: 0 };
      }
      const total = parseFloat(f.total) || 0;
      const pagado = parseFloat(f.total_pagado) || 0;
      map[key].emitido += total;
      map[key].cobrado += pagado;
      map[key].pendiente += Math.max(0, total - pagado);
      map[key].facturas += 1;
    });
    return Object.values(map).sort((a, b) => b.emitido - a.emitido);
  }, [facturasArray, facturadoresArray]);

  // Evolución mensual por facturador (últimos 12 meses)
  const evolucionPorFacturador = useMemo(() => {
    const datos = [];
    const hoy = new Date();
    for (let i = 11; i >= 0; i--) {
      const fecha = new Date(hoy.getFullYear(), hoy.getMonth() - i, 1);
      const mes = MESES_CHART[fecha.getMonth()];
      const año = fecha.getFullYear();
      const punto = { mes: `${mes} ${año.toString().slice(2)}` };
      // Inicializar 0 para cada facturador
      porFacturador.forEach(p => { punto[p.label] = 0; });
      facturasArray.forEach(f => {
        if (!f.fecha_emision) return;
        if (f.estado === 'anulada') return;
        const tipo = (f.tipo || '').toLowerCase();
        if (tipo !== 'factura' && tipo !== 'emitida' && tipo !== 'rectificativa') return;
        const fFecha = new Date(f.fecha_emision);
        if (fFecha.getMonth() !== fecha.getMonth() || fFecha.getFullYear() !== fecha.getFullYear()) return;
        const fid = getFacturadorIdOfFactura(f);
        const fac = fid ? facturadoresArray.find(x => x.id === fid) : null;
        const label = fac ? (fac.prefijo_serie || fac.nombre || `Facturador ${fid}`) : 'Sin facturador';
        if (punto[label] === undefined) punto[label] = 0;
        punto[label] += parseFloat(f.total) || 0;
      });
      datos.push(punto);
    }
    return datos;
  }, [facturasArray, facturadoresArray, porFacturador]);
  
  // Calcular facturación por mes
  const facturacionPorMes = useMemo(() => {
    const datos = {};
    const añoActual = new Date().getFullYear();
    
    // Inicializar todos los meses
    MESES_CHART.forEach((mes, i) => {
      datos[i] = { mes, emitidas: 0, cobradas: 0, pendiente: 0 };
    });
    
    facturasArray.forEach(f => {
      if (!f.fecha_emision) return;
      const fecha = new Date(f.fecha_emision);
      if (fecha.getFullYear() !== añoActual) return;
      
      const mesIdx = fecha.getMonth();
      const total = parseFloat(f.total) || 0;
      const pagado = parseFloat(f.total_pagado) || 0;
      
      if (f.tipo === 'emitida' || f.tipo === 'factura' || f.tipo === 'rectificativa') {
        datos[mesIdx].emitidas += total;
        datos[mesIdx].cobradas += pagado;
        datos[mesIdx].pendiente += (total - pagado);
      }
    });
    
    return Object.values(datos);
  }, [facturasArray]);
  
  // Calcular top clientes por facturación
  const topClientes = useMemo(() => {
    const clienteMap = {};
    
    facturasArray.forEach(f => {
      if (!f.cliente_id || f.estado === 'anulada') return;
      const total = parseFloat(f.total) || 0;
      
      if (!clienteMap[f.cliente_id]) {
        const cliente = contactosArray.find(c => c.id === f.cliente_id);
        clienteMap[f.cliente_id] = {
          id: f.cliente_id,
          nombre: cliente?.nombre || f.cliente_nombre || 'Sin nombre',
          total: 0,
          facturas: 0
        };
      }
      
      clienteMap[f.cliente_id].total += total;
      clienteMap[f.cliente_id].facturas += 1;
    });
    
    return Object.values(clienteMap)
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  }, [facturas, contactos]);
  
  // Calcular distribución por estado
  const distribucionEstados = useMemo(() => {
    const estados = { pendiente: 0, parcial: 0, pagada: 0, vencida: 0, anulada: 0 };
    
    facturasArray.forEach(f => {
      const estado = f.estado || 'pendiente';
      if (estados[estado] !== undefined) {
        estados[estado] += 1;
      }
    });
    
    return Object.entries(estados)
      .filter(([_, v]) => v > 0)
      .map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value
      }));
  }, [facturas]);
  
  // Calcular resumen financiero
  const resumen = useMemo(() => {
    let totalEmitido = 0;
    let totalCobrado = 0;
    let totalPendiente = 0;
    let facturasVencidas = 0;
    
    const hoy = new Date();
    
    facturasArray.forEach(f => {
      if (f.estado === 'anulada') return;
      
      const total = parseFloat(f.total) || 0;
      const pagado = parseFloat(f.total_pagado) || 0;
      
      if (f.tipo === 'emitida' || f.tipo === 'factura' || f.tipo === 'rectificativa') {
        totalEmitido += total;
        totalCobrado += pagado;
        totalPendiente += (total - pagado);
        
        if (f.fecha_vencimiento && new Date(f.fecha_vencimiento) < hoy && pagado < total) {
          facturasVencidas++;
        }
      }
    });
    
    return { totalEmitido, totalCobrado, totalPendiente, facturasVencidas };
  }, [facturas]);
  
  // Evolución mensual (últimos 12 meses)
  const evolucionMensual = useMemo(() => {
    const datos = [];
    const hoy = new Date();
    
    for (let i = 11; i >= 0; i--) {
      const fecha = new Date(hoy.getFullYear(), hoy.getMonth() - i, 1);
      const mes = MESES_CHART[fecha.getMonth()];
      const año = fecha.getFullYear();
      
      let emitido = 0;
      let cobrado = 0;
      
      facturasArray.forEach(f => {
        if (!f.fecha_emision) return;
        const fFecha = new Date(f.fecha_emision);
        if (fFecha.getMonth() === fecha.getMonth() && fFecha.getFullYear() === fecha.getFullYear()) {
          if (f.tipo === 'emitida' || f.tipo === 'factura' || f.tipo === 'rectificativa') {
            emitido += parseFloat(f.total) || 0;
            cobrado += parseFloat(f.total_pagado) || 0;
          }
        }
      });
      
      datos.push({ mes: `${mes} ${año.toString().slice(2)}`, emitido, cobrado });
    }
    
    return datos;
  }, [facturas]);
  
  return (
    <div className="space-y-6">
      {/* Resumen financiero */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Emitido</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(resumen.totalEmitido)}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Cobrado</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(resumen.totalCobrado)}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pendiente Cobro</p>
                <p className="text-2xl font-bold text-orange-600">{formatCurrency(resumen.totalPendiente)}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Facturas Vencidas</p>
                <p className="text-2xl font-bold text-red-600">{resumen.facturasVencidas}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Gráficos principales */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Evolución mensual */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Evolución Mensual (Últimos 12 meses)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={evolucionMensual}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                  <Legend />
                  <Line type="monotone" dataKey="emitido" name="Emitido" stroke="#3B82F6" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="cobrado" name="Cobrado" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        
        {/* Facturación del año actual */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Facturación {new Date().getFullYear()}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={facturacionPorMes}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                  <Legend />
                  <Bar dataKey="emitidas" name="Emitido" fill="#3B82F6" />
                  <Bar dataKey="cobradas" name="Cobrado" fill="#10B981" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Segunda fila de gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Distribución por estado */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Estado de Facturas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={distribucionEstados}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {distribucionEstados.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        
        {/* Top 10 Clientes */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5" />
              Top 10 Clientes por Facturación
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topClientes.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-gray-400">
                No hay datos de facturación
              </div>
            ) : (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {topClientes.map((cliente, i) => (
                  <div key={cliente.id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                    <div className="flex items-center gap-3">
                      <span className={`text-lg font-bold ${i < 3 ? 'text-blue-600' : 'text-gray-400'}`}>
                        #{i + 1}
                      </span>
                      <div>
                        <span className="font-medium">{cliente.nombre}</span>
                        <span className="text-xs text-gray-500 ml-2">({cliente.facturas} facturas)</span>
                      </div>
                    </div>
                    <span className="text-green-600 font-semibold">{formatCurrency(cliente.total)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      {/* Tercera fila: desglose por facturador */}
      {porFacturador.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Tabla resumen por facturador */}
          <Card data-testid="informes-por-facturador">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building className="w-5 h-5" />
                Resumen por Facturador
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b">
                    <tr className="text-xs uppercase text-gray-500">
                      <th className="text-left py-2">Facturador</th>
                      <th className="text-right py-2">Facturas</th>
                      <th className="text-right py-2">Emitido</th>
                      <th className="text-right py-2">Cobrado</th>
                      <th className="text-right py-2">Pendiente</th>
                    </tr>
                  </thead>
                  <tbody>
                    {porFacturador.map(p => (
                      <tr key={p.key} className="border-b hover:bg-gray-50">
                        <td className="py-2 font-medium">{p.label}</td>
                        <td className="text-right">{p.facturas}</td>
                        <td className="text-right font-semibold">{formatCurrency(p.emitido)}</td>
                        <td className="text-right text-green-600">{formatCurrency(p.cobrado)}</td>
                        <td className="text-right text-orange-600">{formatCurrency(p.pendiente)}</td>
                      </tr>
                    ))}
                    {/* Fila de totales */}
                    <tr className="bg-gray-50 font-bold">
                      <td className="py-2">TOTAL</td>
                      <td className="text-right">{porFacturador.reduce((a, p) => a + p.facturas, 0)}</td>
                      <td className="text-right">{formatCurrency(porFacturador.reduce((a, p) => a + p.emitido, 0))}</td>
                      <td className="text-right text-green-700">{formatCurrency(porFacturador.reduce((a, p) => a + p.cobrado, 0))}</td>
                      <td className="text-right text-orange-700">{formatCurrency(porFacturador.reduce((a, p) => a + p.pendiente, 0))}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Comparativa Emitido vs Cobrado por facturador */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Emitido vs Cobrado por Facturador
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={porFacturador}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => formatCurrency(v)} />
                    <Legend />
                    <Bar dataKey="emitido" name="Emitido" fill="#3B82F6" />
                    <Bar dataKey="cobrado" name="Cobrado" fill="#10B981" />
                    <Bar dataKey="pendiente" name="Pendiente" fill="#F59E0B" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Cuarta fila: evolución mensual por facturador (sólo si hay >1) */}
      {porFacturador.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Evolución Mensual por Facturador (Últimos 12 meses)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={evolucionPorFacturador}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                  <Legend />
                  {porFacturador.map((p, i) => (
                    <Line
                      key={p.key}
                      type="monotone"
                      dataKey={p.label}
                      stroke={CHART_COLORS[i % CHART_COLORS.length]}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Contabilidad;
